import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Minimal English speech-to-text on the browser's Web Speech API — no
 * backend, no cost. Finalized phrases stream out through `onTranscript`;
 * callers append them to their input. Feature-detected: `supported` is false
 * on browsers without SpeechRecognition (Firefox), so callers can hide the
 * mic entirely.
 */

// SpeechRecognition is missing from the project's TS dom lib — narrow shims.
interface SpeechRecognitionResultLike {
  isFinal: boolean
  0: { transcript: string }
}

interface SpeechRecognitionEventLike {
  resultIndex: number
  results: ArrayLike<SpeechRecognitionResultLike>
}

interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  start: () => void
  stop: () => void
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

const getRecognitionCtor = (): SpeechRecognitionCtor | null => {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export interface UseSpeechToTextResult {
  supported: boolean
  listening: boolean
  /** Live not-yet-final phrase — show it as a hint while speaking. */
  interimText: string
  toggle: () => void
  stop: () => void
}

export function useSpeechToText(
  onTranscript: (text: string) => void,
): UseSpeechToTextResult {
  const [listening, setListening] = useState(false)
  const [interimText, setInterimText] = useState("")
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  // Keep the latest callback without re-creating the recognition instance.
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript

  const supported = getRecognitionCtor() !== null

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
    recognitionRef.current = null
    setListening(false)
    setInterimText("")
  }, [])

  const toggle = useCallback(() => {
    if (recognitionRef.current) {
      stop()
      return
    }
    const Ctor = getRecognitionCtor()
    if (!Ctor) return
    const recognition = new Ctor()
    recognition.lang = "en-US"
    recognition.continuous = true
    recognition.interimResults = true
    recognition.onresult = event => {
      let interim = ""
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcript = result[0]?.transcript ?? ""
        if (result.isFinal) {
          if (transcript.trim()) onTranscriptRef.current(transcript.trim())
        } else {
          interim += transcript
        }
      }
      setInterimText(interim)
    }
    // Browser auto-ends on silence / permission denial — reflect it.
    recognition.onend = () => {
      recognitionRef.current = null
      setListening(false)
      setInterimText("")
    }
    recognition.onerror = () => {
      recognitionRef.current = null
      setListening(false)
      setInterimText("")
    }
    recognitionRef.current = recognition
    setListening(true)
    recognition.start()
  }, [stop])

  // Never leave the mic hot after unmount.
  useEffect(() => stop, [stop])

  return { supported, listening, interimText, toggle, stop }
}
